/**
 * TypeScript shapes for the Knowledge Base REST surface (PR-A).
 *
 * Mirrors the Zod schemas in `apps/api/src/knowledge/dtos/*.ts`. The DTOs
 * are the source of truth — when an API change lands these types follow.
 *
 * Intentionally hand-typed (not imported from the API package) so the
 * dashboard bundle stays NestJS-free, matching the agents/api/types.ts
 * convention at apps/dashboard/src/agents/api/types.ts.
 */

// ----- Enums -----

export type ChunkStrategy = "fixed_window" | "semantic" | "markdown_aware";
export type SyncCadence = "hourly" | "daily" | "weekly" | "off";
export type SourceType =
  | "upload"
  | "url"
  | "sitemap"
  | "paste"
  | "notion"
  | "confluence"
  | "gdrive"
  | "s3";
export type SourceStatus = "pending_upload" | "uploaded" | "ingesting" | "ingested" | "failed";
export type JobState = "queued" | "running" | "completed" | "failed";
export type JobKind = "ingest" | "resync";
export type JobTrigger = "initial" | "manual" | "cron" | "webhook" | "config_change";
export type EmbeddingProvider = "openai" | "voyage" | "cohere" | "google";
export type RetrievalMode = "embed_in_prompt" | "as_skill";

// ----- Knowledge Base -----

export interface KnowledgeBase {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  /** FDE-authored, visitor-facing status line shown while this KB is searched. */
  status_label: string | null;
  embedding_model_id: string;
  chunk_strategy: ChunkStrategy;
  chunk_size_tokens: number;
  chunk_overlap_tokens: number;
  vector_top_n: number;
  keyword_top_n: number;
  hybrid_search: boolean;
  min_rerank_score: number;
  contextual_chunking: boolean;
  query_rewrite_enabled: boolean;
  sync_cadence: SyncCadence;
  last_sync_at: string | null;
  sources_count: number;
  chunks_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateKnowledgeBaseRequest {
  name: string;
  description?: string;
  status_label?: string | null;
  embedding_model_id?: string;
  chunk_strategy?: ChunkStrategy;
  chunk_size_tokens?: number;
  chunk_overlap_tokens?: number;
  vector_top_n?: number;
  keyword_top_n?: number;
  hybrid_search?: boolean;
  min_rerank_score?: number;
  contextual_chunking?: boolean;
  query_rewrite_enabled?: boolean;
  sync_cadence?: SyncCadence;
}

export type UpdateKnowledgeBaseRequest = Partial<CreateKnowledgeBaseRequest>;

// ----- Source — discriminated by source_type -----

export interface CreateUploadSourceRequest {
  source_type: "upload";
  name: string;
  filename: string;
  content_type:
    | "text/plain"
    | "text/markdown"
    | "application/pdf"
    | "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  byte_size: number;
}

export interface CreateUrlSourceRequest {
  source_type: "url";
  name: string;
  url: string;
  wait_for_ms?: number;
  include_selectors?: string[];
  exclude_selectors?: string[];
  only_main_content?: boolean;
  generate_suggestions?: boolean;
  suggestions_count?: number;
  suggestions_model?: string;
}

export interface CreateSitemapSourceRequest {
  source_type: "sitemap";
  name: string;
  url: string;
  max_pages?: number;
  max_depth?: number;
  include_paths?: string[];
  exclude_paths?: string[];
  wait_for_ms?: number;
  include_selectors?: string[];
  exclude_selectors?: string[];
  only_main_content?: boolean;
  generate_suggestions?: boolean;
  suggestions_count?: number;
  suggestions_model?: string;
}

export interface CreatePasteSourceRequest {
  source_type: "paste";
  name: string;
  text: string;
  source_url?: string;
}

export type CreateSourceRequest =
  | CreateUploadSourceRequest
  | CreateUrlSourceRequest
  | CreateSitemapSourceRequest
  | CreatePasteSourceRequest;

export interface KnowledgeSource {
  id: string;
  knowledge_base_id: string;
  workspace_id: string;
  name: string;
  source_type: SourceType;
  status: SourceStatus;
  content_type_declared: string | null;
  byte_size_declared: number | null;
  external_url: string | null;
  last_ingest_run_id: string | null;
  tags: string[];
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  last_error_code: string | null;
  last_error_message: string | null;
}

export interface UploadCreateResponse {
  source_id: string;
  presigned_put_url: string;
  expires_at: string;
  max_bytes: number;
}

export interface InlineCreateResponse {
  source: KnowledgeSource;
  job_id: string;
}

export type CreateSourceResponse = UploadCreateResponse | InlineCreateResponse;

export function isUploadCreateResponse(r: CreateSourceResponse): r is UploadCreateResponse {
  return "presigned_put_url" in r;
}

export interface FinalizeSourceResponse {
  source_id: string;
  status: "uploaded" | "ingesting";
}

// ----- Source pages (extracted-markdown viewer) -----

export interface SourcePageSummary {
  seq: number;
  url: string | null;
  title: string | null;
  byte_size: number;
}

export interface SourcePageContent {
  seq: number;
  url: string | null;
  title: string | null;
  markdown: string;
}

// ----- Source chunks (retrieval-chunk viewer) -----

export interface SourceChunk {
  seq: number;
  text: string;
  heading_path: string[];
  char_start: number;
  char_end: number;
  char_count: number;
  embedding_model_id: string;
  embedding_dim: number;
}

export interface SourceChunkStats {
  count: number;
  avg_chars: number;
  min_chars: number;
  max_chars: number;
  chunk_strategy: string;
}

export interface PagedSourceChunks {
  items: SourceChunk[];
  next_cursor: string | null;
  stats: SourceChunkStats;
}

// ----- Page-suggestion pool (KB-level Suggestions table) -----

export interface PageSuggestion {
  id: string;
  knowledge_source_id: string;
  page_seq: number;
  path: string;
  url: string | null;
  title: string | null;
  candidates: string[];
  picked: string[];
  enabled: boolean;
  model_provider: string | null;
  model_id: string | null;
  generated_at: string;
}

export interface UpdatePageSuggestionRequest {
  candidates?: string[];
  picked?: string[];
  enabled?: boolean;
}

/** Optional generation overrides: model (catalogue key "provider/id") and
 *  pool size for this run. */
export interface RegenerateSuggestionsRequest {
  model?: string;
  count?: number;
}

export interface ListPageSuggestionsParams extends ListParams {
  source_id?: string;
}

export interface RegeneratePageSuggestionsResponse {
  pages_written: number;
}

// ----- Pattern suggestion rules (operator-authored, URL-matched) -----

export type SuggestionMatchType = "exact" | "glob" | "contains" | "regex";

export interface SuggestionRule {
  id: string;
  knowledge_base_id: string;
  match_type: SuggestionMatchType;
  pattern: string;
  candidates: string[];
  picked: string[];
  enabled: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CreateSuggestionRuleRequest {
  match_type?: SuggestionMatchType;
  pattern: string;
  candidates?: string[];
  picked?: string[];
  enabled?: boolean;
  sort_order?: number;
}

export type UpdateSuggestionRuleRequest = Partial<CreateSuggestionRuleRequest>;

// ----- Jobs -----

export interface KbJob {
  id: string;
  knowledge_base_id: string;
  knowledge_source_id: string | null;
  kind: JobKind;
  state: JobState;
  trigger: JobTrigger;
  started_at: string | null;
  ended_at: string | null;
  chunks_count: number | null;
  bytes_in: number | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
}

// ----- Retrieval playground -----

export interface KbQueryFilter {
  source_ids?: string[];
  tags?: string[];
  source_types?: string[];
  created_after?: string;
  created_before?: string;
  metadata?: Record<string, string>;
}

export interface KbQueryRequest {
  query: string;
  top_k?: number;
  filter?: KbQueryFilter;
}

export interface KbChunkHit {
  chunk_id: string;
  knowledge_base_id: string;
  knowledge_source_id: string;
  text: string;
  source_url: string | null;
  score: number;
  heading_path: string[];
  char_count: number;
  embedding_model_id: string | null;
  embedding_dim: number | null;
}

export interface KbRetrievalSummary {
  embedding_model_id: string;
  chunk_strategy: string;
  candidates_scanned: number;
  returned: number;
  min_rerank_score: number;
  hybrid_used: boolean;
  vector_candidates: number;
  keyword_candidates: number;
  reranker_disabled: boolean;
  reranker_skipped: boolean;
  reranker_fallback: boolean;
}

export interface KbQueryResponse {
  chunks: KbChunkHit[];
  no_match: boolean;
  retrieval: KbRetrievalSummary;
}

// ----- KB-meta (embedding-model catalogue) -----
// SHAPE NOTE: excludes `api_key_env_var` per NFR-SEC-4 (parent unit). Do
// not add the field here — adding it would diverge from the server DTO.

export interface KbMetaModel {
  id: string;
  provider: EmbeddingProvider;
  dimensions: number;
  default_batch_size: number;
  default_concurrency: number;
  max_input_tokens: number;
  label: string;
  notes: string;
}

export interface ListKbMetaModelsResponse {
  items: KbMetaModel[];
}

// ----- Agent attachments -----

export interface AgentKnowledgeBaseAttachment {
  agent_id: string;
  knowledge_base_id: string;
  retrieval_mode: RetrievalMode;
  top_k: number;
  attached_at: string;
  /** Provenance (ENG-36201): `mention` rows are owned by the publish reconciler. */
  attached_via: "manual" | "mention";
}

export interface AttachKnowledgeBaseRequest {
  knowledge_base_id: string;
  retrieval_mode: RetrievalMode;
  top_k?: number;
}

// ----- Pagination -----

export interface Paged<T> {
  items: T[];
  next_cursor: string | null;
}

export interface ListParams {
  cursor?: string;
  limit?: number;
}

export interface ListKbParams extends ListParams {
  q?: string;
}

export interface ListSourcesParams extends ListParams {
  status?: SourceStatus;
}

export interface ListJobsParams extends ListParams {
  state?: JobState;
  kind?: JobKind;
}
